import getEnabledItems from '@lyearn/core/utils/array/getEnabledItems';
import { TFunction } from '@lyearn/i18n';
import {
  Article,
  BookOutline,
  BulbOutline,
  Chat,
  CheckmarkSquare,
  DoneAll,
  Edit2Outline,
  Flag2Outline,
  FolderOutline,
  Mobile2Outline,
  SurveyOutline,
  TvFill,
  Ufo,
} from '@lyearn/icons';
import { FeatureFlags, FeatureFlagsMap } from '@/helper/FeatureFlags';
import { AppConfig } from '@/hooks/useAppConfig';
import { ResourceTypeEnum, SearchableContentTypeEnum } from '@/types/schema';

export const getContentTypeOptions = (
  t: TFunction,
  config: AppConfig,
  featureFlag: FeatureFlagsMap,
  contentTypes?: ResourceTypeEnum[],
) => {
  const enabledItems = getEnabledItems([
    {
      Icon: BookOutline,
      value: ResourceTypeEnum.Course,
      label: t('Common:ResourceType.Course'),
      enabled: true,
    },
    {
      Icon: Flag2Outline,
      value: ResourceTypeEnum.Path,
      label: t('Common:ResourceType.Path'),
      enabled: true,
    },
    {
      Icon: Article,
      value: ResourceTypeEnum.Article,
      label: t('Common:ResourceType.Article'),
      enabled: true,
    },
    {
      Icon: Mobile2Outline,
      value: ResourceTypeEnum.MicroLearning,
      label: t('Common:ResourceType.MicroLearning'),
      enabled: featureFlag[FeatureFlags.EnableMicroLearning],
    },
    {
      Icon: Chat,
      value: ResourceTypeEnum.Roleplay,
      label: t('Common:ResourceType.Roleplay'),
      enabled: featureFlag[FeatureFlags.EnableRoleplay],
    },
    {
      Icon: Edit2Outline,
      value: ResourceTypeEnum.Exercise,
      label: t('Common:ResourceType.Exercise'),
      enabled: true,
    },
    {
      Icon: DoneAll,
      value: ResourceTypeEnum.Task,
      label: t('Common:Task'),
      enabled: config?.site.isTaskEnabled ?? true,
    },
    {
      Icon: CheckmarkSquare,
      value: ResourceTypeEnum.Quiz,
      label: t('Common:ResourceType.Quiz'),
      enabled: true,
    },
    {
      Icon: Ufo,
      value: ResourceTypeEnum.ExternalContent,
      label: t('Common:ResourceType.ExternalContent'),
      enabled: featureFlag[FeatureFlags.EnableExternalContent],
    },
    {
      Icon: FolderOutline,
      value: ResourceTypeEnum.Scorm,
      label: t('Common:ResourceType.Scorm'),
      enabled: config?.ui.showImportCreateBtn ?? true,
    },
    {
      Icon: SurveyOutline,
      value: ResourceTypeEnum.Survey,
      label: t('Common:ResourceType.Survey'),
      enabled: true,
    },
    {
      Icon: BulbOutline,
      value: ResourceTypeEnum.KnowledgeAsset,
      label: t('Common:ResourceType.KnowledgeAsset'),
      enabled: featureFlag[FeatureFlags.EnableKnowledgeAsset],
    },
  ]);

  if (contentTypes) {
    const contentTypesSet = new Set(contentTypes);
    return enabledItems.filter((it) => contentTypesSet.has(it.value));
  }

  return enabledItems;
};

export const getSearchContentTypeOptions = (
  t: TFunction,
  isSessionEnabled: boolean,
  featureFlag: FeatureFlagsMap,
  config: AppConfig,
  allowedContentTypes: Set<SearchableContentTypeEnum>,
) => {
  return getEnabledItems([
    {
      Icon: BookOutline,
      value: SearchableContentTypeEnum.Course,
      label: t('Common:ResourceType.Course'),
      enabled: allowedContentTypes.has(SearchableContentTypeEnum.Course),
    },
    {
      Icon: Flag2Outline,
      value: SearchableContentTypeEnum.Path,
      label: t('Common:ResourceType.Path'),
      enabled: allowedContentTypes.has(SearchableContentTypeEnum.Path),
    },
    {
      Icon: Article,
      value: SearchableContentTypeEnum.Article,
      label: t('Common:ResourceType.Article'),
      enabled: allowedContentTypes.has(SearchableContentTypeEnum.Article),
    },
    {
      Icon: Mobile2Outline,
      value: SearchableContentTypeEnum.MicroLearning,
      label: t('Common:ResourceType.MicroLearning'),
      enabled: allowedContentTypes.has(SearchableContentTypeEnum.MicroLearning),
    },
    {
      Icon: Chat,
      value: SearchableContentTypeEnum.Roleplay,
      label: t('Common:ResourceType.Roleplay'),
      enabled: allowedContentTypes.has(SearchableContentTypeEnum.Roleplay),
    },
    {
      Icon: Ufo,
      value: SearchableContentTypeEnum.ExternalContent,
      label: t('Common:ResourceType.ExternalContent'),
      enabled:
        featureFlag[FeatureFlags.EnableExternalContent] &&
        allowedContentTypes.has(SearchableContentTypeEnum.ExternalContent),
    },
    {
      Icon: FolderOutline,
      value: ResourceTypeEnum.Scorm,
      label: t('Common:ResourceType.Scorm'),
      enabled:
        config?.ui.showImportCreateBtn && allowedContentTypes.has(SearchableContentTypeEnum.Scorm),
    },
    {
      Icon: TvFill,
      value: SearchableContentTypeEnum.Event,
      label: t('Common:ResourceType.Session'),
      enabled: isSessionEnabled,
    },
    {
      Icon: Ufo,
      value: SearchableContentTypeEnum.KnowledgeAsset,
      label: t('Common:ResourceType.KnowledgeAsset'),
      enabled: allowedContentTypes.has(SearchableContentTypeEnum.KnowledgeAsset),
    },
  ]);
};
